import React, { useRef, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from 'chart.js';
import { useDispatch, useSelector } from 'react-redux';
import { getLogs } from '../../redux/actions/logs';
import { authanticatedUser } from '../../utils/helper';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

// Custom CSS for loading spinner
const spinnerStyles = `
  .custom-spinner {
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-top: 4px solid #0E3C6E;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    animation: spin 1s linear infinite;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  @keyframes spin {
    0% { transform: translate(-50%, -50%) rotate(0deg); }
    100% { transform: translate(-50%, -50%) rotate(360deg); }
  }
`;

const ClientsLogInActivitiesChart = () => {
  const chartRef = useRef(null);
  const dispatch = useDispatch();
  const { logs, loading } = useSelector((state) => state.logs);

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });

  useEffect(() => {
    if (authanticatedUser?.token) {
      dispatch(getLogs({ token: authanticatedUser.token }));
    }
  }, [dispatch]);

  useEffect(() => {
    if (!logs || loading) return;

    // Filter logs for Customer type
    const loginFail = logs.filter(
      (log) => log.action === 'Customer login attempt' && log?.type?.toLowerCase() === 'customer'
    );
    const loginSuccess = logs.filter(
      (log) => log.action === 'Customer login' && log?.type?.toLowerCase() === 'customer'
    );
    const passwordResets = logs.filter(
      (log) => log.action === 'Password Change Request' && log?.type?.toLowerCase() === 'customer'
    );
    const totalLoginAttempts = logs.filter(
      (log) =>
        log.type === 'Customer' &&
        ['Customer login attempt', 'Customer login'].includes(log.action)
    );
    const statementDownloads = logs.filter(
      (log) => log.action === 'Statement Download' && log.type === 'Customer'
    );

    // Aggregate by month
    const aggregateByMonth = (logArray) => {
      const counts = {};
      logArray.forEach((log) => {
        const date = new Date(log.createdAt.$date || log.createdAt);
        const monthYear = date.toLocaleString('en-US', { month: 'short', year: 'numeric' });
        counts[monthYear] = (counts[monthYear] || 0) + 1;
      });
      return counts;
    };

    const failCounts = aggregateByMonth(loginFail);
    const successCounts = aggregateByMonth(loginSuccess);
    const resetCounts = aggregateByMonth(passwordResets);
    const totalAttemptCounts = aggregateByMonth(totalLoginAttempts);
    const downloadCounts = aggregateByMonth(statementDownloads);

    // Get unique month-year labels, sorted chronologically
    const allMonths = [
      ...new Set([
        ...Object.keys(failCounts),
        ...Object.keys(successCounts),
        ...Object.keys(resetCounts),
        ...Object.keys(totalAttemptCounts),
        ...Object.keys(downloadCounts),
      ]),
    ].sort((a, b) => {
      const dateA = new Date(`01 ${a}`);
      const dateB = new Date(`01 ${b}`);
      return dateA - dateB;
    });

    // Create chart data
    const chart = chartRef.current;
    let gradientStroke1, gradientStroke2, gradientStroke3, gradientStroke4, gradientStroke5;

    if (chart) {
      const ctx = chart.canvas.getContext('2d');
      gradientStroke1 = ctx.createLinearGradient(0, 230, 0, 50);
      gradientStroke1.addColorStop(1, 'rgba(116, 123, 124, 0.5)');
      gradientStroke1.addColorStop(0.4, 'rgba(96, 99, 108, 0.25)');
      gradientStroke1.addColorStop(0, 'rgba(96, 99, 108, 0)');

      gradientStroke2 = ctx.createLinearGradient(0, 230, 0, 50);
      gradientStroke2.addColorStop(1, 'rgba(14, 60, 110, 0.5)');
      gradientStroke2.addColorStop(0.4, 'rgba(27, 93, 150, 0.25)');
      gradientStroke2.addColorStop(0, 'rgba(27, 93, 150, 0)');

      gradientStroke3 = ctx.createLinearGradient(0, 230, 0, 50);
      gradientStroke3.addColorStop(1, 'rgba(123, 31, 162, 0.5)');
      gradientStroke3.addColorStop(0.4, 'rgba(156, 39, 176, 0.25)');
      gradientStroke3.addColorStop(0, 'rgba(156, 39, 176, 0)');

      gradientStroke4 = ctx.createLinearGradient(0, 230, 0, 50);
      gradientStroke4.addColorStop(1, 'rgba(46, 125, 50, 0.5)');
      gradientStroke4.addColorStop(0.4, 'rgba(76, 175, 80, 0.25)');
      gradientStroke4.addColorStop(0, 'rgba(76, 175, 80, 0)');

      gradientStroke5 = ctx.createLinearGradient(0, 230, 0, 50);
      gradientStroke5.addColorStop(1, 'rgba(255, 87, 34, 0.5)');
      gradientStroke5.addColorStop(0.4, 'rgba(255, 138, 87, 0.25)');
      gradientStroke5.addColorStop(0, 'rgba(255, 138, 87, 0)');
    }

    setChartData({
      labels: allMonths,
      datasets: [
        {
          label: 'Failed Login Attempts',
          tension: 0.4,
          pointRadius: 0,
          borderColor: '#747B7C',
          borderWidth: 3,
          backgroundColor: gradientStroke1,
          fill: true,
          data: allMonths.map((month) => failCounts[month] || 0),
          maxBarThickness: 6,
        },
        {
          label: 'Successful Logins',
          tension: 0.4,
          pointRadius: 0,
          borderColor: '#0E3C6E',
          borderWidth: 3,
          backgroundColor: gradientStroke2,
          fill: true,
          data: allMonths.map((month) => successCounts[month] || 0),
          maxBarThickness: 6,
        },
        {
          label: 'Password Resets',
          tension: 0.4,
          pointRadius: 0,
          borderColor: '#7B1FA2',
          borderWidth: 3,
          backgroundColor: gradientStroke3,
          fill: true,
          data: allMonths.map((month) => resetCounts[month] || 0),
          maxBarThickness: 6,
        },
        {
          label: 'Total Login Attempts',
          tension: 0.4,
          pointRadius: 0,
          borderColor: '#2E7D32',
          borderWidth: 3,
          backgroundColor: gradientStroke4,
          fill: true,
          data: allMonths.map((month) => totalAttemptCounts[month] || 0),
          maxBarThickness: 6,
        },
        {
          label: 'Statement Downloads',
          tension: 0.4,
          pointRadius: 0,
          borderColor: '#FF5722',
          borderWidth: 3,
          backgroundColor: gradientStroke5,
          fill: true,
          data: allMonths.map((month) => downloadCounts[month] || 0),
          maxBarThickness: 6,
        },
      ],
    });
  }, [logs, loading]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        labels: {
          color: 'rgb(0, 0, 0)',
          font: {
            size: 12,
            family: 'Open Sans',
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: { family: 'Open Sans', size: 12 },
        bodyFont: { family: 'Open Sans', size: 11 },
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 10,
        cornerRadius: 8,
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
    scales: {
      y: {
        grid: {
          drawBorder: false,
          display: true,
          drawOnChartArea: true,
          drawTicks: false,
          borderDash: [5, 5],
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          display: true,
          padding: 10,
          color: '#0E3C6E',
          font: {
            size: 11,
            family: 'Open Sans',
            style: 'normal',
            lineHeight: 2,
          },
          stepSize: 1,
          beginAtZero: true,
        },
      },
      x: {
        grid: {
          drawBorder: false,
          display: false,
          drawOnChartArea: false,
          drawTicks: false,
        },
        ticks: {
          display: true,
          color: '#0E3C6E',
          padding: 20,
          font: {
            size: 11,
            family: 'Open Sans',
            style: 'normal',
            lineHeight: 2,
          },
        },
      },
    },
  };

  return (
    <div style={{ position: 'relative', height: '300px', width: '100%' }}>
      <style>{spinnerStyles}</style>
      {loading && <div className="custom-spinner"></div>}
      <Line ref={chartRef} data={chartData} options={options} />
    </div>
  );
};

export default ClientsLogInActivitiesChart;